import { FC } from 'react';
import { format } from 'date-fns';
import { cn } from '../../../utils/cn';
import { CheckCircle, Clock, XCircle, Play } from 'lucide-react';

interface Run {
  id: string;
  startDate: string;
  endDate: string;
  runType: string;
  status: string;
  totalPayments: number;
  discrepancyCount: number;
  completedAt: string | null;
}

interface ReconciliationRunHistoryProps {
  runs: Run[];
}

function getStatusConfig(status: string): {
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
} {
  const configs: Record<
    string,
    { icon: React.ReactNode; bgColor: string; textColor: string }
  > = {
    COMPLETED: {
      icon: <CheckCircle className="h-4 w-4" />,
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      textColor: 'text-green-800 dark:text-green-300',
    },
    IN_PROGRESS: {
      icon: <Play className="h-4 w-4" />,
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      textColor: 'text-blue-800 dark:text-blue-300',
    },
    PENDING: {
      icon: <Clock className="h-4 w-4" />,
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
      textColor: 'text-yellow-800 dark:text-yellow-300',
    },
    FAILED: {
      icon: <XCircle className="h-4 w-4" />,
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      textColor: 'text-red-800 dark:text-red-300',
    },
  };

  return (
    configs[status] || {
      icon: <Clock className="h-4 w-4" />,
      bgColor: 'bg-gray-100 dark:bg-slate-700',
      textColor: 'text-gray-800 dark:text-slate-300',
    }
  );
}

export const ReconciliationRunHistory: FC<ReconciliationRunHistoryProps> = ({ runs }) => {
  if (runs.length === 0) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center">
        <p className="text-gray-500 dark:text-slate-400">No reconciliation runs yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {runs.map((run) => {
        const statusConfig = getStatusConfig(run.status);

        return (
          <div
            key={run.id}
            className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-slate-100">
                  {format(new Date(run.startDate), 'MMM d')} -{' '}
                  {format(new Date(run.endDate), 'MMM d, yyyy')}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                  {run.totalPayments} payments
                  {run.discrepancyCount > 0 && (
                    <span className="text-red-600 dark:text-red-400">
                      {' '}
                      &bull; {run.discrepancyCount} discrepancies
                    </span>
                  )}
                </p>
              </div>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                  statusConfig.bgColor,
                  statusConfig.textColor
                )}
              >
                {statusConfig.icon}
                {run.status}
              </span>
            </div>
            {run.completedAt && (
              <p className="mt-2 text-xs text-gray-400 dark:text-slate-500">
                Completed: {format(new Date(run.completedAt), 'PPp')}
              </p>
            )}
            <div className="mt-2">
              <span
                className={cn(
                  'inline-block rounded px-2 py-0.5 text-xs',
                  run.runType === 'MANUAL'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                )}
              >
                {run.runType}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
